import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Filter } from 'src/app/core/services/base/database.service';
import { Column } from '../../table.component';

@Component({
  selector: 'app-filter-text',
  templateUrl: './filter-text.component.html',
  styleUrls: ['./filter-text.component.scss'],
})
export class FilterTextComponent implements OnInit, OnDestroy, OnChanges {
  @Input('column') column!: Column;
  @Output('columnChanges') columnChanges: EventEmitter<Column> =
    new EventEmitter();

  currentColumn!: Column;

  operator = 'contains';
  text: string = '';

  operators = [
    { value: 'eq',         label: 'FilterOperatorIs' },
    { value: 'neq',        label: 'FilterOperatorIsNot' },
    { value: 'contains',   label: 'FilterOperatorContains' },
    { value: 'ncontains',  label: 'FilterOperatorNotContains' },
    { value: 'startswith', label: 'FilterOperatorStartsWith' },
    { value: 'endswith',   label: 'FilterOperatorEndsWith' },
    { value: 'empty',      label: 'FilterOperatorEmpty' },
    { value: 'nempty',     label: 'FilterOperatorNotEmpty' },
  ];

  subscriptions: Subscription[] = [];

  filters: Filter[] = [];

  constructor() {}

  ngOnInit(): void {
    this.currentColumn = this.column;
    this._restoreUiFromFilters(this.column.filters);
  }

  // 'empty'/'nempty' don't take a value — the operator alone is the whole filter.
  isValuelessOperator(op: string): boolean {
    return op === 'empty' || op === 'nempty';
  }

  // Restores operator/text in the UI from the persisted filter list.
  // Uses the first visible filter that has a non-null value — and, for operators that
  // take a value, a non-empty trimmed one — so that reopening the column panel always
  // reflects what is currently applied.
  private _restoreUiFromFilters(filters: Filter[]): void {
    const firstActive = filters.find((f) => {
      if (!f.visible || f.value == null || typeof f.value === 'object') return false;
      return this.isValuelessOperator(f.operator) || String(f.value).trim() !== '';
    });
    if (firstActive) {
      this.text = this.isValuelessOperator(firstActive.operator) ? '' : String(firstActive.value);
      this.operator = firstActive.operator;
    } else {
      this.text = '';
      this.operator = 'contains';
    }
  }

  private createNewFilter(): Filter {
    // 'empty'/'nempty' ignore the value entirely downstream (applyFilter checks IS
    // NULL, not the value) — but a real value is still required here because both
    // applyFilter() and getFiltersFromColumns() treat a null/undefined value as "no
    // filter" and drop it. Use a fixed marker so no leftover typed text leaks in.
    const isValueless = this.isValuelessOperator(this.operator);
    // config.parseFilterValue lets a column normalize what the user typed before it
    // hits the query — e.g. a digits-only column (CPF) still needs to match input
    // typed with punctuation, since ilike does no normalization itself.
    const parse = this.column.config?.parseFilterValue as ((raw: string) => string) | undefined;
    const rawValue = parse && !isValueless ? parse(this.text) : this.text;
    return {
      column: this.column.id,
      operator: this.operator as any,
      value: isValueless ? true : rawValue,
      visible: true,
    };
  }

  private _emitChanges(): void {
    this.columnChanges.emit({ ...this.currentColumn, filters: [...this.filters] });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['column'] && changes['column'].currentValue) {
      this.currentColumn = changes['column'].currentValue;
      // Clone to avoid mutating shared column reference (breaks Angular CD).
      this.filters = this.currentColumn.filters ? [...this.currentColumn.filters] : [];
      // Restore UI inputs to reflect persisted state when the panel re-opens.
      this._restoreUiFromFilters(this.filters);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription?.unsubscribe());
  }

  getOperatorLabel(op: string): string {
    return this.operators.find((o) => o.value === op)?.label || '';
  }

  clearFilter() {
    this.filters = [];
    this.text = '';
    this.operator = 'contains';
    this._emitChanges();
  }

  addFilter() {
    // Ignore Apply when there is no text — prevents persisting empty filters that
    // would still flag the column as filtered. Doesn't apply to 'empty'/'nempty',
    // which have no value to type in the first place.
    if (!this.isValuelessOperator(this.operator) && (!this.text || !this.text.trim())) return;

    // Replace the active filter for this column instead of stacking duplicates.
    this.filters = [this.createNewFilter()];
    this._emitChanges();
  }

  removeFilter(index: number) {
    this.filters.splice(index, 1);
    this._restoreUiFromFilters(this.filters);
    this._emitChanges();
  }

  onTextChange(text: string): void {
    this.text = text;
  }
}
